import { Component, Input } from "@angular/core";
import { BaseFilterCellComponent, FilterService } from "@progress/kendo-angular-grid";
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { Align, Collision, Margin } from '@progress/kendo-angular-popup';

@Component({
    selector: 'date-range-filter-cell',
    styles: [`
        :host {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            box-sizing: border-box;
        }

        kendo-daterange {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            white-space: nowrap;
        }

        kendo-daterange > kendo-dateinput.range-filter {
            flex: 0 0 120px;
            width: 120px;
            min-width: 120px;
        }

        .range-filter-separator {
            flex: 0 0 auto;
            margin: 0 4px;
        }

        .k-button {
            margin-left: 5px;
            flex: 0 0 auto;
        }
    ` ],
    template: `
        <kendo-daterange>

						<kendo-dateinput
							class="range-filter"
							kendoDateRangeStartInput
							[value]="rangeStart !== undefined ? rangeStart : start"
							(valueChange)="onStartChange($event)">
						</kendo-dateinput>
						<span class="range-filter-separator">-</span>
						<kendo-dateinput
                            class="range-filter"
                            kendoDateRangeEndInput
                            [value]="rangeEnd !== undefined ? rangeEnd : end"
                            (valueChange)="onEndChange($event)">
						</kendo-dateinput>
            <kendo-daterange-popup
                [anchorAlign]="anchorAlign"
                [popupAlign]="popupAlign"
                [collision]="popupCollision"
                [margin]="popupMargin"
                [animate]="false"
                (close)="onPopupClose()">
                <ng-template kendoDateRangePopupTemplate>
                    <kendo-multiviewcalendar kendoDateRangeSelection>
                        <kendo-multiviewcalendar-messages today="Сегодня">
                        </kendo-multiviewcalendar-messages>
                    </kendo-multiviewcalendar>
                </ng-template>
            </kendo-daterange-popup>

        </kendo-daterange>
        <button
            *ngIf="hasFilter"
            class="k-button k-button-icon"
            title="Очистить"
            (click)="clearFilter()">
            <span class="k-icon k-i-filter-clear"></span>
        </button>
    `
})
export class DateRangeFilterCellComponent extends BaseFilterCellComponent {
    @Input()
    public filter: CompositeFilterDescriptor;

    @Input()
    public field: string;

    public rangeStart: Date;
    public rangeEnd: Date;

    constructor(filterService: FilterService) {
        super(filterService);
    }

    public anchorAlign: Align = { horizontal: 'right', vertical: 'bottom' };
    public popupAlign: Align = { horizontal: 'right', vertical: 'top' };
    public popupCollision: Collision = { horizontal: 'fit', vertical: 'flip' };
    public popupMargin: Margin = { horizontal: 6, vertical: 8 };
    public get start(): Date {
        const first = this.findByOperator("gte");

        return (first || <FilterDescriptor>{}).value;
    }

    public get end(): Date {
        const end = this.findByOperator("lte");
        return (end || <FilterDescriptor>{}).value;
    }

    public get hasFilter(): boolean {
        return this.filtersByField(this.field).length > 0;
    }

    public onStartChange(value: Date): void {
        if (value && value.getFullYear() < 2000) {
            return;
        }

        this.rangeStart = value;
    }

    public onEndChange(value: Date): void {
        if (value && value.getFullYear() < 2000) {
            return;
        }

        this.rangeEnd = value;
    }

    public onPopupClose(): void {
        this.filterRange(this.rangeStart, this.rangeEnd);
    }

    public clearFilter(): void {
        this.rangeStart = null;
        this.rangeEnd = null;

        this.filterService.filter(
            this.removeFilter(this.field)
        );
    }

    public filterRange(start: Date, end: Date): void {
        const currentStart = this.start;
        const currentEnd = this.end;

        if (this.isSameDate(start, currentStart) && this.isSameDate(end, currentEnd)) {
            return;
        }

        if ((start && start.getFullYear() < 2000) || (end && end.getFullYear() < 2000)) {
            return;
        }

        this.filter = this.removeFilter(this.field);

        const filters = [];

        if (start) {
            filters.push({
                field: this.field,
                operator: "gte",
                value: start
            });
        }

        if (end) {
            filters.push({
                field: this.field,
                operator: "lte",
                value: end
            });
        }

        const root = this.filter || {
            logic: "and",
            filters: []
        };

        if (filters.length) {
            root.filters.push(...filters);
        }

        this.filterService.filter(root);
    }

    private isSameDate(a: Date, b: Date): boolean {
        if (!a && !b) {
            return true;
        }

        if (!a || !b) {
            return false;
        }

        return new Date(a).getTime() === new Date(b).getTime();
    }

    private findByOperator(op: string): FilterDescriptor {
        return this.filtersByField(this.field)
            .filter(({ operator }) => operator === op)[0];
    }
}
